/* Copyright (c) 2020-present CloudIO Inc */

import {
  buttonSize,
  buttonVariant,
} from 'componentregistry/registry/constants';
import { Item } from '../../registry/Component.type';
import { toolTipProps } from 'componentregistry/utils/commonProps';
import { page as SamplePage } from '../../help/sample_pages/SamplePageSimple';
import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import {
  ComponentManagerFn,
  DefaultExpressionValue,
} from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { defaultValue } from './ButtonDropdown';
import { isValidButtonColor } from '../Button/Button';

const page: {
  form: Item<'Form'>;
  root: Item<'Page'>;
  flexItem: Item<'FlexItem'>;
  actionShowMessage: Item<'ActionShowMessage'>;
  rowPanel: Item<'RowPanel'>;
  flexPanel: Item<'FlexPanel'>;
  columnPanel: Item<'ColumnPanel'>;
  columnPanel1: Item<'ColumnPanel'>;
  columnPanel2: Item<'ColumnPanel'>;
  triggerOnClick: Item<'TriggerOnClick'>;
  dataStoreReference: Item<'DataStoreReference'>;
  localDataStore: Item<'LocalDataStore'>;
  actionCreate: Item<'ActionCreate'>;
  actionClear: Item<'ActionClear'>;
  triggerOnPageActive: Item<'TriggerOnPageActive'>;
  button: Item<'ButtonDropdownChild'>;
  button2: Item<'ButtonDropdownChild'>;
  button3: Item<'ButtonDropdownChild'>;
} = {
  ...SamplePage,
  button: {
    itemId: 'button',
    props: {
      text: { type: 'text', value: 'Action One' },
      icon: { iconName: 'check' },
    },
    children: ['triggerOnClick'],
    type: 'ButtonDropdownChild',
    parent: 'buttonDropdown',
  },
  button2: {
    itemId: 'button2',
    props: {
      text: { type: 'text', value: 'Action Two' },
      icon: { iconName: 'check' },
    },
    children: [],
    type: 'ButtonDropdownChild',
    parent: 'buttonDropdown',
  },
  button3: {
    itemId: 'button3',
    props: {
      text: { type: 'text', value: 'Action Three' },
      icon: { iconName: 'expand' },
    },
    children: [],
    type: 'ButtonDropdownChild',
    parent: 'buttonDropdown',
  },
};

const ButtonDropdownManager: ComponentManagerFn<'ButtonDropdown'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'ButtonDropdown'>(platform, 'ButtonDropdown'),
    type: 'ButtonDropdown',
    name: 'Button Dropdown',
    isChildAllowed: (_page, item, childType) =>
      (['ButtonDropdownChild', 'MenuItemDivider'] as ItemType[]).includes(
        childType,
      ),
    defaultValue,
    defaultChildren: ['ButtonDropdownChild', 'ButtonDropdownChild'],
    props: {
      disableCondition: {
        label: 'Disable Condition',
        type: 'expressionOnly',
        returnType: () => 'boolean',
      },
      fullWidth: {
        label: 'Full Width',
        type: 'boolean',
        helpText:
          'If checked, the button will take up the full width of its container.',
      },
      size: {
        label: 'Size',
        type: 'select',
        options: buttonSize,
      },
      text: {
        label: 'Text',
        type: 'expression',
        returnType: async () => 'string',
      },
      startIcon: {
        label: 'Start Icon',
        type: 'iconselect',
        helpText: 'Icon to be placed before the text.',
        parentColorProp: 'color',
      },
      color: {
        label: 'Color',
        type: 'ThemeBGColor',
        filterColor: isValidButtonColor,
      },
      variant: {
        label: 'Variant',
        type: 'select',
        options: buttonVariant,
      },
      ...toolTipProps,
    },
    getDisplayText: (item) => item.props.text.value,
    category: 'Toolbar',
    icon: { iconName: 'io-button-dropdown', iconType: 'cloudio' },
    description: `**Overview**<br/>
The Button Dropdown component combines the functionality of a standard button with a dropdown menu. It displays a primary button (with text and an optional icon) that, when clicked, reveals a list of secondary actions.
Use this component to group related commands under a single header to save space and reduce clutter, such as an "Export" button that reveals options for "PDF," "Excel," and "CSV."

**1. Structure & Children**<br/>
The component acts as a container for its menu items.
* **Allowed Children:** ButtonDropdownChild (The individual clickable items inside the menu) and MenuItemDivider (A visual separator to group related items within the list).
* **Default Content:** When added, it automatically initializes with two ButtonDropdownChild items.

**2. Configuration**<br/>
* **Text:** The label displayed on the main trigger button (e.g., "Actions" or "Options").
* **Start Icon:** An optional icon displayed to the left of the button text.
* **Variant:** Controls the button's visual style (Contained, Outlined, or Text), matching the standard Button component.
* **Color:** Sets the theme color (e.g., primary, secondary) for the main button.
* **Size:** Adjustable button size (small, medium, large).

**3. Advanced Behavior**<br/>
* **Disable Condition:** An expression that disables the entire dropdown button, preventing access to any of its sub-items.
* **Full Width:** If enabled, the button expands to fill the width of its container.
* **Tooltip:** Supports standard tooltips to explain the button's purpose on hover.

**4. Best Use Cases**<br/>
* **Action Menus:** Grouping "Edit," "Delete," and "Duplicate" under a single "Manage" button.
* **Export Options:** A "Download" button that offers multiple file formats.
* **State Changes:** A "Change Status" button that lists "In Progress," "Completed," and "On Hold."`,
    helpRenders: [
      {
        title: 'Default Button Dropdown',
        description: 'Button Dropdown rendered with Default Properties',
        page,
        exampleId: 'e1',
        clearParentChildren: true,
        hideProps: true,
        item: {
          itemId: 'buttonDropdown',
          props: {
            text: DefaultExpressionValue,
          },
          children: ['button', 'button2', 'button3'],
          type: 'ButtonDropdown',
          parent: 'columnPanel1',
        },
      },
      {
        title: 'Button Dropdown Variants',
        description: 'Button Dropdown rendered with different variant',
        page,
        clearParentChildren: true,
        exampleId: 'e3',
        visibleProps: ['variant'],
        item: {
          itemId: 'buttonDropdown',
          props: {
            variant: 'outlined',
            text: { type: 'text', value: 'Choose Action' },
            startIcon: { iconName: 'check' },
          },
          children: ['button', 'button2', 'button3'],
          type: 'ButtonDropdown',
          parent: 'columnPanel1',
        },
      },
      {
        title: 'Button Dropdown Colors',
        description: 'Button Dropdown rendered with different theme color',
        page,
        exampleId: 'e4',
        clearParentChildren: true,
        visibleProps: ['color'],
        item: {
          itemId: 'buttonDropdown',
          props: {
            color: 'secondary',
            text: { type: 'text', value: 'Choose Action' },
            startIcon: { iconName: 'check' },
          },
          children: ['button', 'button2', 'button3'],
          type: 'ButtonDropdown',
          parent: 'columnPanel1',
        },
      },
    ],
  };
};

export default ButtonDropdownManager;
